import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useSmartPolling } from '@/hooks/useSmartPolling';
import { POLLING_INTERVALS } from '@/lib/performanceConfig';
import { equipesDoLider, filtrarPeloLider } from '../triagem/triagemPedidoUtils';
import { useUserProfileContext } from '@/contexts/UserProfileContext';
import { usePedidoMovement } from '@/hooks/usePedidoMovement';
import { depoisDoClique, manterNaTriagem, meuUsuarioId } from '@/components/kanban/triagem/acaoTriagem';

// A fila: VALIDAÇÃO PENDENTE (Tramitação Vivo). Os destinos são as colunas do
// status_catalogo (Análise manual, Erro de input, Inspeção concluída).
const QUADRO_ORIGEM = 'TRAMITACAO_VIVO';
const COLUNA_ORIGEM = 'VALIDAÇÃO PENDENTE';

// Nova interface focada em ITEM individual
export interface ItemTriagemCotacao {
  item_id: string;
  pedido_id: string;
  numero_pedido: string;
  cnpj: string;
  /** CNPJ ou CPF — a fila mostra um segmento por vez (`segmentoTriagem.ts`). */
  tipo_pessoa: string | null;
  /** CNPJ ou CPF, como está no banco. Máscara em `formatarDocumento`. */
  documento: string;
  razao_social: string;
  produto: string;
  quantidade: number;
  plano_valor: number;
  subtotal: number;
  entrada_coluna_em: string;
  numero_cotacao_operadora: string;
  cliente_id: string | null;
  is_first_of_cnpj: boolean;
}

export function useTriagemCotacao() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profile } = useUserProfileContext();
  const { refetchInterval, onQueryError, onQuerySuccess } = useSmartPolling({
    weight: 'B',
    baseInterval: POLLING_INTERVALS.PESO_B,
  });

  // Buscar ITENS na coluna VALIDAÇÃO PENDENTE (não mais pedidos agrupados)
  const { data: itens = [], isLoading, refetch } = useQuery({
    queryKey: ['triagem-cotacao'],
    queryFn: async () => {
      const lider = await equipesDoLider(profile);
      const { data, error } = await filtrarPeloLider(supabase
        .from('v_planilha_mae')
        .select('item_id, pedido_id, numero_pedido, cnpj, razao_social, entrada_coluna_em, numero_cotacao_operadora, produto, quantidade, plano_valor, subtotal, manter_em, cliente_id, tipo_pessoa, documento')
        .eq('coluna_atual', 'VALIDAÇÃO PENDENTE')
        .order('manter_em', { ascending: true, nullsFirst: true })
        .order('cnpj', { ascending: true })
        .order('pedido_id', { ascending: true })
        .order('entrada_coluna_em', { ascending: true }), profile, lider);

      if (error) {
        onQueryError(error);
        throw error;
      }

      onQuerySuccess();

      // Processar para adicionar is_first_of_cnpj
      let lastCnpj = '';
      const itensProcessados: ItemTriagemCotacao[] = (data || []).map(item => {
        const isFirst = item.cnpj !== lastCnpj;
        lastCnpj = item.cnpj || '';
        return {
          item_id: item.item_id,
          pedido_id: item.pedido_id,
          numero_pedido: String(item.numero_pedido || ''),
          cnpj: item.cnpj || '',
          tipo_pessoa: item.tipo_pessoa ?? null,
          documento: item.documento || item.cnpj || '',
          razao_social: item.razao_social || '',
          produto: item.produto || '',
          quantidade: Number(item.quantidade) || 0,
          plano_valor: Number(item.plano_valor) || 0,
          subtotal: Number((item as any).subtotal) || (Number(item.plano_valor) * Number(item.quantidade || 1)),
          entrada_coluna_em: item.entrada_coluna_em || '',
          numero_cotacao_operadora: item.numero_cotacao_operadora || '',
          cliente_id: (item as any).cliente_id || null,
          is_first_of_cnpj: isFirst,
        };
      });

      return itensProcessados;
    },
    refetchInterval,
  });

  const { moverPedidoJunto } = usePedidoMovement();

  // Helper: buscar todos os itens da mesma cotação
  const getItensByCotacao = (cotacao: string): ItemTriagemCotacao[] => {
    if (!cotacao) return [];
    return itens.filter(i => i.numero_cotacao_operadora === cotacao);
  };

  const porPedido = (linhas: ItemTriagemCotacao[]) => {
    const grupos = new Map<string, string[]>();
    for (const l of linhas) grupos.set(l.pedido_id, [...(grupos.get(l.pedido_id) ?? []), l.item_id]);
    return [...grupos.entries()];
  };

  /* A cotação anda inteira numa ida por pedido (fn_mover_pedido_junto, que já
     grava o historico_status). Só anda a linha que ainda está na fila; o pedido
     que outra pessoa já tirou daqui não derruba os outros da mesma cotação. */
  const moverCotacao = async (linhas: ItemTriagemCotacao[], toQuadro: string, toColuna: string, motivo: string) => {
    const resultados = await Promise.allSettled(porPedido(linhas).map(([pedidoId, itensIds]) => moverPedidoJunto({
      pedidoId, itensIds, quadroOrigem: QUADRO_ORIGEM, colunaOrigem: COLUNA_ORIGEM,
      toQuadro: toQuadro as Parameters<typeof moverPedidoJunto>[0]['toQuadro'], toColuna, motivo, origemTriagem: true,
    })));
    const ok = resultados.filter((r): r is PromiseFulfilledResult<Awaited<ReturnType<typeof moverPedidoJunto>>> => r.status === 'fulfilled');
    if (ok.length === 0) throw (resultados[0] as PromiseRejectedResult).reason;
    const movidos = ok.flatMap((r) => (r.value.movidos ?? []).map((m) => m.item_id));
    return { count: movidos.length, movidos };
  };

  // Só as linhas que o banco confirmou saem (ou descem) da tela na hora.
  const depoisDeAndar = (itemClicado: ItemTriagemCotacao, itensIds: string[], modo: 'sai' | 'desce') =>
    depoisDoClique(queryClient, ['triagem-cotacao'], { pedidoId: itemClicado.pedido_id, itens: itensIds }, modo);

  // Mutation para mover para ANALISE MANUAL CREDITO (todos da mesma cotação)
  const moverParaAnaliseManual = useMutation({
    mutationFn: async (itemId: string) => {
      const itemClicado = itens.find(i => i.item_id === itemId);
      if (!itemClicado) throw new Error('Item não encontrado');

      const cotacao = itemClicado.numero_cotacao_operadora;

      // Validação: cotação obrigatória para análise manual
      if (!cotacao) {
        throw new Error('Cotação obrigatória! O item precisa ter cotação para ir para Análise Manual.');
      }

      const itensCotacao = getItensByCotacao(cotacao);
      const { count, movidos } = await moverCotacao(itensCotacao, 'TRAMITACAO_PRIME', 'ANALISE MANUAL CREDITO', 'Triagem Cotação - Análise Manual');
      return { count, cotacao, itemClicado, movidos };
    },
    onSuccess: ({ count, itemClicado, movidos }) => {
      const label = count > 1 ? `${count} itens da mesma cotação movidos` : 'Item movido';
      toast({
        title: 'Análise Manual',
        description: `${label} para análise manual de crédito.`,
      });
      void depoisDeAndar(itemClicado, movidos, 'sai');
    },
    onError: (error) => {
      console.error('Erro ao mover para análise manual:', error);
      toast({
        title: 'Erro ao mover item',
        description: error instanceof Error ? error.message : 'Tente novamente.',
        variant: 'destructive',
      });
      void queryClient.invalidateQueries({ queryKey: ['triagem-cotacao'] });
    },
  });

  // Mutation para mover para NEGOCIAÇÃO (ERRO DE INPUT) (todos da mesma cotação)
  const moverParaErroInput = useMutation({
    mutationFn: async ({ itemId, textoOperadora }: { itemId: string; textoOperadora: string }) => {
      const itemClicado = itens.find(i => i.item_id === itemId);
      if (!itemClicado) throw new Error('Item não encontrado');
      if (!textoOperadora || textoOperadora.trim().length < 10) {
        throw new Error('Justificativa obrigatória: cole o texto do erro que veio da operadora.');
      }

      // A nota com o retorno da operadora entra ANTES: sem ela, a cotação não anda.
      const corpo = `⚠️ ERRO DE INPUT${itemClicado.numero_cotacao_operadora ? ` — Cotação ${itemClicado.numero_cotacao_operadora}` : ''}\nRetorno da operadora: ${textoOperadora.trim()}`;
      const { error: notaError } = await (supabase as any).from('mensagem').insert({
        cliente_id: itemClicado.cliente_id,
        pedido_id: itemClicado.pedido_id,
        canal: 'interno',
        direcao: 'out',
        corpo,
        criado_por: await meuUsuarioId(),
        status: 'enviado',
      });
      if (notaError) throw new Error(`Não foi possível registrar o erro na timeline: ${notaError.message || notaError.code || 'erro'}`);

      const cotacao = itemClicado.numero_cotacao_operadora;
      const itensCotacao = cotacao ? getItensByCotacao(cotacao) : [itemClicado];
      const { count, movidos } = await moverCotacao(itensCotacao, 'TRAMITACAO_VIVO', 'NEGOCIAÇÃO (ERRO DE INPUT)', 'Triagem Cotação - Erro de Input');
      return { count, itemClicado, movidos };
    },
    onSuccess: ({ count, itemClicado, movidos }) => {
      const label = count > 1 ? `${count} itens da mesma cotação enviados` : 'Item enviado';
      toast({
        title: 'Erro de Input',
        description: `${label} para Negociação (Erro de Input).`,
      });
      void depoisDeAndar(itemClicado, movidos, 'sai');
    },
    onError: (error) => {
      console.error('Erro ao mover para erro input:', error);
      toast({
        title: 'Erro ao mover item',
        description: error instanceof Error ? error.message : 'Tente novamente.',
        variant: 'destructive',
      });
      void queryClient.invalidateQueries({ queryKey: ['triagem-cotacao'] });
    },
  });

  // Mutation para mover para INSPEÇÃO CONCLUIDA (aprovado) — todos da mesma cotação
  const moverParaInspecaoConcluida = useMutation({
    mutationFn: async (itemId: string) => {
      const itemClicado = itens.find(i => i.item_id === itemId);
      if (!itemClicado) throw new Error('Item não encontrado');

      const cotacao = itemClicado.numero_cotacao_operadora;
      const itensCotacao = cotacao ? getItensByCotacao(cotacao) : [itemClicado];
      const { count, movidos } = await moverCotacao(itensCotacao, 'PRE_ATIVO', 'INSPEÇÃO CONCLUIDA', 'Triagem Cotação - Aprovado');
      return { count, itemClicado, movidos };
    },
    onSuccess: ({ count, itemClicado, movidos }) => {
      const label = count > 1 ? `${count} itens da mesma cotação movidos` : 'Item movido';
      toast({
        title: 'Cotação Aprovada',
        description: `${label} para pré-ativo. Pedido seguro!`,
      });
      void depoisDeAndar(itemClicado, movidos, 'sai');
    },
    onError: (error) => {
      console.error('Erro ao mover para inspeção concluída:', error);
      toast({
        title: 'Erro ao mover item',
        description: error instanceof Error ? error.message : 'Tente novamente.',
        variant: 'destructive',
      });
      void queryClient.invalidateQueries({ queryKey: ['triagem-cotacao'] });
    },
  });

  // Mutation para mover para o final da fila (MANTER) — todos da mesma cotação,
  // uma ida por pedido (fn_triagem_manter só desce a linha que ainda está aqui)
  const moverParaFinal = useMutation({
    mutationFn: async (itemId: string) => {
      const itemClicado = itens.find(i => i.item_id === itemId);
      if (!itemClicado) throw new Error('Item não encontrado');

      const cotacao = itemClicado.numero_cotacao_operadora;
      const itensCotacao = cotacao ? getItensByCotacao(cotacao) : [itemClicado];
      const r = await Promise.allSettled(porPedido(itensCotacao).map(([pedidoId, ids]) =>
        manterNaTriagem({ pedidoId, itens: ids, coluna: COLUNA_ORIGEM })));
      const ok = r.filter((x): x is PromiseFulfilledResult<Awaited<ReturnType<typeof manterNaTriagem>>> => x.status === 'fulfilled');
      if (ok.length === 0) throw (r[0] as PromiseRejectedResult).reason;

      const movidos = ok.flatMap((x) => x.value.itens ?? []);
      return { count: movidos.length, itemClicado, movidos };
    },
    onSuccess: ({ count, itemClicado, movidos }) => {
      const label = count > 1 ? `${count} itens movidos` : 'Item movido';
      toast({ title: 'Manter', description: `${label} para o final da fila.` });
      void depoisDeAndar(itemClicado, movidos, 'desce');
    },
    onError: (error) => {
      toast({ title: 'Erro', description: error instanceof Error ? error.message : 'Tente novamente.', variant: 'destructive' });
      void queryClient.invalidateQueries({ queryKey: ['triagem-cotacao'] });
    },
  });

  return {
    itens,
    isLoading,
    refetch,
    moverParaAnaliseManual: moverParaAnaliseManual.mutateAsync,
    moverParaErroInput: moverParaErroInput.mutateAsync,
    moverParaInspecaoConcluida: moverParaInspecaoConcluida.mutateAsync,
    moverParaFinal: moverParaFinal.mutateAsync,
  };
}
